import React from 'react';

// Matches a leading "@agent", "agent:" or "ask (the) agent" trigger and returns
// the remaining question. Returns null when the message isn't aimed at the agent.
const AGENT_TRIGGER = /^\s*(?:@agent|agent\s*[:,\-]|ask\s+(?:the\s+)?agent\b[:,\-]?)\s*/i;
export const extractAgentQuery = (text: string): string | null => {
  if (!text) return null;
  const m = text.match(AGENT_TRIGGER);
  if (!m) return null;
  return text.slice(m[0].length).trim();
};

// Detects an Azure DevOps epic/feature reference in the user's message and returns
// the work item id. Supports full AZDO urls, "epic/feature 12345", "#12345" and a
// bare number typed on its own.
export const extractWorkItemId = (text: string): string | null => {
  if (!text) return null;
  const urlMatch = text.match(/_workitems\/edit\/(\d+)/i);
  if (urlMatch) return urlMatch[1];
  const kwMatch = text.match(/\b(?:epic|feature|story|work\s*item)\b[^0-9]{0,15}(\d{2,})/i);
  if (kwMatch) return kwMatch[1];
  const hashMatch = text.match(/(?:^|\s)#(\d{2,})\b/);
  if (hashMatch) return hashMatch[1];
  const bareMatch = text.trim().match(/^(\d{2,})$/);
  if (bareMatch) return bareMatch[1];
  return null;
};

// Turns the backend welcome message into nicely rendered markdown: the inline
// "• " separators are exploded into a real bullet list (with a blank line after
// the intro line and before the closing call-to-action) so it renders cleanly.
export const formatWelcomeMessage = (raw: string): string => {
  if (!raw || raw.indexOf('\u2022') === -1) return raw;
  return raw
    .replace(/\s*\u2022\s*/g, '\n- ')   // each "• item" becomes its own list item
    .replace(/:[ \t]*\n- /, ':\n\n- ')   // blank line between the intro and the list
    .trim();
};

// Decodes the handful of HTML entities the agent emits so they read as plain text.
const decodeHtmlEntities = (s: string): string =>
  s
    .replace(/&nbsp;/gi, ' ')
    .replace(/&amp;/gi, '&')
    .replace(/&lt;/gi, '<')
    .replace(/&gt;/gi, '>')
    .replace(/&quot;/gi, '"')
    .replace(/&#0?39;/gi, "'")
    .replace(/&apos;/gi, "'")
    .replace(/&#(\d+);/g, (_, d) => String.fromCodePoint(Number(d)));

// The Copilot Studio agent's final replies (e.g. the "work items created"
// confirmation) arrive as raw HTML — anchors, <br> breaks and basic emphasis.
// Teams renders HTML, but the chat UI renders markdown (raw HTML is disabled),
// so convert the common tags to their markdown equivalents and strip the rest.
export const agentTextToMarkdown = (raw: string): string => {
  if (!raw || !/<[a-z!/][\s\S]*?>/i.test(raw)) return raw;
  let out = raw
    .replace(/<\s*br\s*\/?\s*>/gi, '\n')
    .replace(/<\/\s*p\s*>/gi, '\n\n')
    .replace(/<\s*p[^>]*>/gi, '')
    .replace(/<\/\s*li\s*>/gi, '\n')
    .replace(/<\s*li[^>]*>/gi, '- ')
    .replace(/<\/?\s*(?:ul|ol)[^>]*>/gi, '\n')
    .replace(/<\s*(?:b|strong)\s*>([\s\S]*?)<\/\s*(?:b|strong)\s*>/gi, '**$1**')
    .replace(/<\s*(?:i|em)\s*>([\s\S]*?)<\/\s*(?:i|em)\s*>/gi, '*$1*')
    .replace(/<\s*a\b[^>]*?href\s*=\s*["']([^"']+)["'][^>]*>([\s\S]*?)<\/\s*a\s*>/gi, '[$2]($1)')
    .replace(/<[^>]+>/g, ''); // drop any remaining tags
  out = decodeHtmlEntities(out);
  return out.replace(/[ \t]+\n/g, '\n').replace(/\n{3,}/g, '\n\n').trim();
};

// Flattens rendered markdown children into plain text so we can inspect a cell's value.
export const nodeToText = (node: React.ReactNode): string => {
  if (node == null || typeof node === 'boolean') return '';
  if (typeof node === 'string' || typeof node === 'number') return String(node);
  if (Array.isArray(node)) return node.map(nodeToText).join('');
  if (React.isValidElement(node)) return nodeToText((node.props as { children?: React.ReactNode })?.children);
  return '';
};

// Loose comparison of table cell text (ignores markdown emphasis and spacing).
export const normalizeForMatch = (s: string): string =>
  s.toLowerCase().replace(/[*_`]/g, '').replace(/\s+/g, ' ').trim();

// Escapes text for safe insertion into HTML built for epic comments.
export const escHtml = (v: string | null | undefined): string =>
  (v == null ? '' : String(v)).replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;');

// Splits long text into <= maxLen chunks without ever cutting mid-sentence.
// Boundaries are tried in order of preference: paragraph -> line -> sentence ->
// word, so a chunk always ends on a complete line/paragraph (and never inside a
// sentence) while staying under the character limit. Mid-word cutting is used
// only as an absolute last resort for a single "word" longer than maxLen.
export const splitTextIntoChunks = (text: string, maxLen: number): string[] => {
  if (text.length <= maxLen) return [text];

  // Greedily packs units into chunks joined by `joiner`. Any unit that is still
  // longer than maxLen is broken down further with `splitUnit` (the next-finer
  // boundary) so we never emit an over-limit chunk by cutting a sentence.
  const pack = (
    units: string[],
    joiner: string,
    splitUnit: (u: string) => string[],
  ): string[] => {
    const out: string[] = [];
    let current = "";
    const flush = () => { if (current.trim()) out.push(current.trim()); current = ""; };

    for (const unit of units) {
      if (unit.length > maxLen) {
        flush();
        out.push(...splitUnit(unit));
        continue;
      }
      if (current && (current + joiner + unit).length > maxLen) flush();
      current = current ? current + joiner + unit : unit;
    }
    flush();
    return out;
  };

  // Last resort: a single sentence longer than maxLen is split on word
  // boundaries; a single word longer than maxLen is hard-split by characters.
  const splitBySentence = (paragraph: string): string[] =>
    pack(paragraph.split(/(?<=[.!?])\s+/), " ", sentence =>
      pack(sentence.split(/\s+/), " ", word => {
        const parts: string[] = [];
        for (let i = 0; i < word.length; i += maxLen) parts.push(word.slice(i, i + maxLen));
        return parts;
      }),
    );

  // A paragraph over the limit is split into lines/bullets before sentences.
  const splitByLine = (paragraph: string): string[] =>
    pack(paragraph.split(/(?=•)|\n/), "\n", splitBySentence);

  // Top level: prefer paragraph boundaries (blank-line separated).
  return pack(text.split(/\n{2,}/), "\n\n", splitByLine);
};

export const blobToDataUrl = (blob: Blob): Promise<string> =>
  new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onloadend = () => resolve(reader.result as string);
    reader.onerror = reject;
    reader.readAsDataURL(blob);
  });

// Reads a file into raw base64 (no `data:...;base64,` prefix) for the ui-mapping API.
export const fileToBase64 = async (file: File): Promise<string> => {
  const dataUrl = await blobToDataUrl(file);
  return dataUrl.replace(/^data:[^;]*;base64,/, '');
};
